import { useContext, useState } from 'react';
import { GlobalContext } from 'providers/GlobalProvider';

// --- Shared "run in the background" (Design Assistant UX plan §9C): save the ticket first, then hand the
// run to the server with a `notify` block saying which ticket to update and who to email, then confirm with
// the dialog once the server acknowledges (`notify_scheduled`). Extracted from Beneficial Ownership with
// identical behaviour, so a fix here applies to every workflow that uses it. Each workflow still builds its
// own request around the notify block (BO adds `command: run_all` + `notify_on_complete`).

/** The request's `notify` block (pyapi app/workflow/background_run.py NotifyContext). */
export interface BackgroundRunNotify {
  ticket_id: string;
  ticket_name: string;
  recipient_email?: string;
  recipient_name: string;
  // The Keycloak id drives the in-app (bell) notification's recipient filter.
  recipient_kc_id?: string;
  group_id?: string;
  group_name?: string;
  // The run's id, the same one its live step spends carry, so the background run's credit
  // spend skips steps already billed. Beneficial Ownership leaves it out (its bo_id is the id).
  run_id?: string;
}

export const buildBackgroundRunNotify = (
  curUser: unknown,
  ticketId: string,
  ticketName: string,
): BackgroundRunNotify => {
  const user = (curUser || {}) as any;
  const recipientName = [user.basics?.firstName, user.basics?.lastName].filter(Boolean).join(' ');
  return {
    ticket_id: ticketId,
    ticket_name: ticketName,
    recipient_email: user.basics?.email,
    recipient_name: recipientName,
    recipient_kc_id: user.basics?.id,
    group_id: user.selected_team?.id,
    group_name: user.selected_team?.name,
  };
};

export interface StartBackgroundRun {
  /** Saves the ticket (as In Progress) and returns its id; a falsy id stops here (the save shows its own error). */
  save: () => Promise<string | null | undefined>;
  /** The ticket's name, read after the save. */
  ticketName: () => string;
  /** Runs after a successful save, before the request (e.g. echo the user's choice in the chat). */
  beforeSend?: () => void;
  /** Sends the workflow's own request carrying the notify block. */
  send: (notify: BackgroundRunNotify) => void;
}

export interface BackgroundRun {
  /** Save first, then hand the run to the server. False when the save failed and nothing was sent. */
  start: (run: StartBackgroundRun) => Promise<boolean>;
  dialogOpen: boolean;
  /** Call when the server acknowledges with `notify_scheduled`. */
  showDialog: () => void;
  closeDialog: () => void;
}

export const useBackgroundRun = (): BackgroundRun => {
  const { cur_user } = useContext(GlobalContext);
  const [dialogOpen, setDialogOpen] = useState(false);

  const start = async ({ save, ticketName, beforeSend, send }: StartBackgroundRun): Promise<boolean> => {
    const ticketId = await save();
    if (!ticketId) return false;
    if (beforeSend) beforeSend();
    send(buildBackgroundRunNotify(cur_user, ticketId, ticketName()));
    return true;
  };

  return {
    start: start,
    dialogOpen: dialogOpen,
    showDialog: () => setDialogOpen(true),
    closeDialog: () => setDialogOpen(false),
  };
};
